import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import MappingNotes from './MappingNotes';

const API = process.env.REACT_APP_API_URL || '';

const STATUS_OPTIONS = [
  { value: 'sourcing', label: 'Sourcing', color: 'bg-amber-100 text-amber-700' },
  { value: 'sourced', label: 'Sourced', color: 'bg-emerald-100 text-emerald-700' },
];
const PRIORITY_OPTIONS = [
  { key: 'none', label: 'None', dot: 'bg-gray-300', bg: 'bg-gray-50 border-gray-200 text-gray-600' },
  { key: 'high', label: 'High', dot: 'bg-red-500', bg: 'bg-red-50 border-red-300 text-red-700' },
  { key: 'medium', label: 'Med', dot: 'bg-yellow-400', bg: 'bg-yellow-50 border-yellow-300 text-yellow-700' },
  { key: 'low', label: 'Low', dot: 'bg-green-500', bg: 'bg-green-50 border-green-300 text-green-700' },
];

// Which builtin columns are sortable, and their sort keys.
const BUILTIN_SORT = { status: 'status', priority: 'priority', assigned: 'assigned', candidates: 'candidates', last_added: 'last_added' };
// Per-column width classes (builtin ids + fallback for custom).
const COL_WIDTH = { status: 'w-28', priority: 'w-24', assigned: 'w-32', candidates: 'w-24', linkedin: 'w-36', last_added: 'w-32', notes: 'min-w-[180px]' };

function authHeaders() {
  const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

export default function ResearchTab({ pipelineId, searchName, onBulkContext }) {
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [firms, setFirms] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [notesOpen, setNotesOpen] = useState(true);
  const [notesWidth, setNotesWidth] = useState(null); // px; null = compute 30% on mount
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [draggedCol, setDraggedCol] = useState(null);
  const draggingRef = useRef(false);

  const toggleSel = useCallback((id) => {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);
  const clearSel = useCallback(() => setSelected(new Set()), []);

  // Divider drag to resize the notes panel + initialize to 30%.
  useEffect(() => {
    const container = document.getElementById('mapping-split');
    if (container && notesWidth == null) {
      setNotesWidth(Math.round(container.getBoundingClientRect().width * 0.30));
    }
    const onMove = (e) => {
      if (!draggingRef.current) return;
      const c = document.getElementById('mapping-split');
      if (!c) return;
      const rect = c.getBoundingClientRect();
      const fromRight = rect.right - e.clientX;
      setNotesWidth(Math.max(240, Math.min(rect.width * 0.6, fromRight)));
    };
    const onUp = () => { draggingRef.current = false; document.body.style.cursor = ''; document.body.style.userSelect = ''; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [notesWidth]);

  // Load board + reference data.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [board, firmsR, usersR] = await Promise.all([
          fetch(`${API}/api/research/${pipelineId}`, { headers: authHeaders() }).then(r => r.json()),
          fetch(`${API}/api/firms`, { headers: authHeaders() }).then(r => r.json()).catch(() => []),
          fetch(`${API}/api/users`, { headers: authHeaders() }).then(r => r.json()).catch(() => []),
        ]);
        if (cancelled) return;
        setRows(board.rows || []);
        setColumns(board.columns || []);
        setFirms(Array.isArray(firmsR) ? firmsR : (firmsR.firms || []));
        const allUsers = Array.isArray(usersR) ? usersR : (usersR.users || []);
        // Assignees are internal org users only — exclude client users.
        setUsers(allUsers.filter(u => u.user_type === 'recruitment' || u.user_type === 'admin'));
      } catch (e) {
        console.error('research load failed', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pipelineId]);

  // ── Row mutations (optimistic) ────────────────────────────────────────────
  const patchRow = useCallback(async (rowId, patch) => {
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, ...patch } : r));
    try {
      const r = await fetch(`${API}/api/research/${pipelineId}/rows/${rowId}`, {
        method: 'PATCH', headers: authHeaders(), body: JSON.stringify(patch),
      });
      const updated = await r.json();
      setRows(prev => prev.map(x => x.id === rowId ? { ...x, ...updated } : x));
    } catch (e) { console.error('patch row failed', e); }
  }, [pipelineId]);

  // Multi-assign: add/remove a user on a row (optimistic).
  const addAssignee = useCallback((rowId, user) => {
    setRows(prev => prev.map(r => {
      if (r.id !== rowId) return r;
      const existing = r.assignees || [];
      if (existing.some(a => a.id === user.id)) return r;
      return { ...r, assignees: [...existing, user] };
    }));
    fetch(`${API}/api/research/${pipelineId}/rows/${rowId}/assignees`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ user_id: user.id }),
    }).catch(e => console.error('assign failed', e));
  }, [pipelineId]);

  const removeAssignee = useCallback((rowId, userId) => {
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, assignees: (r.assignees || []).filter(a => a.id !== userId) } : r));
    fetch(`${API}/api/research/${pipelineId}/rows/${rowId}/assignees/${userId}`, {
      method: 'DELETE', headers: authHeaders(),
    }).catch(e => console.error('unassign failed', e));
  }, [pipelineId]);

  const patchCustom = useCallback((rowId, key, value) => {
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, custom: { ...(r.custom || {}), [key]: value } } : r));
    fetch(`${API}/api/research/${pipelineId}/rows/${rowId}`, {
      method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ customKey: key, customValue: value }),
    }).catch(e => console.error('patch custom failed', e));
  }, [pipelineId]);

  const addRow = useCallback(async (firm) => {
    const body = firm
      ? { firm_id: firm.id, firm_name: firm.name, status: 'sourcing' }
      : { firm_name: '', status: 'sourcing' };
    const r = await fetch(`${API}/api/research/${pipelineId}/rows`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
    });
    const created = await r.json();
    setRows(prev => [...prev, created]);
  }, [pipelineId]);

  const deleteRow = useCallback(async (rowId) => {
    setRows(prev => prev.filter(r => r.id !== rowId));
    setSelected(prev => { const n = new Set(prev); n.delete(rowId); return n; });
    fetch(`${API}/api/research/${pipelineId}/rows/${rowId}`, { method: 'DELETE', headers: authHeaders() }).catch(() => {});
  }, [pipelineId]);

  // ── Bulk actions on selected rows ─────────────────────────────────────────
  const bulkPatch = useCallback(async (patch) => {
    const ids = Array.from(selected);
    setRows(prev => prev.map(r => ids.includes(r.id) ? { ...r, ...patch } : r));
    await Promise.all(ids.map(id =>
      fetch(`${API}/api/research/${pipelineId}/rows/${id}`, {
        method: 'PATCH', headers: authHeaders(), body: JSON.stringify(patch),
      }).catch(() => {})
    ));
  }, [selected, pipelineId]);

  const bulkDelete = useCallback(async () => {
    const ids = Array.from(selected);
    setRows(prev => prev.filter(r => !ids.includes(r.id)));
    clearSel();
    await Promise.all(ids.map(id =>
      fetch(`${API}/api/research/${pipelineId}/rows/${id}`, { method: 'DELETE', headers: authHeaders() }).catch(() => {})
    ));
  }, [selected, pipelineId, clearSel]);

  // Lift selection + bulk actions up so the parent can render the bulk bar in
  // the pageStrip (overlaying the tab nav) like every other page.
  useEffect(() => {
    if (!onBulkContext) return;
    onBulkContext(selected.size > 0 ? {
      count: selected.size,
      statusOptions: STATUS_OPTIONS,
      priorityOptions: PRIORITY_OPTIONS.filter(p => p.key !== 'none'),
      setStatus: (v) => bulkPatch({ status: v }),
      setPriority: (v) => bulkPatch({ priority: v }),
      clearPriority: () => bulkPatch({ priority: null }),
      remove: () => bulkDelete(),
      clear: () => clearSel(),
    } : null);
  }, [selected, onBulkContext, bulkPatch, bulkDelete, clearSel]);

  const addColumn = useCallback(async (label, kind) => {
    const r = await fetch(`${API}/api/research/${pipelineId}/columns`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ label, kind }),
    });
    const col = await r.json();
    setColumns(prev => [...prev, col]);
  }, [pipelineId]);

  const deleteColumn = useCallback(async (colId) => {
    setColumns(prev => prev.filter(c => c.id !== colId));
    setRows(prev => prev.map(r => { const c = { ...(r.custom || {}) }; delete c[colId]; return { ...r, custom: c }; }));
    fetch(`${API}/api/research/${pipelineId}/columns/${colId}`, { method: 'DELETE', headers: authHeaders() }).catch(() => {});
  }, [pipelineId]);

  // Reorder a custom column (drag & drop). Persists new positions.
  const reorderColumn = useCallback((fromId, toId) => {
    if (fromId === toId) return;
    setColumns(prev => {
      const arr = [...prev];
      const fromIdx = arr.findIndex(c => c.id === fromId);
      const toIdx = arr.findIndex(c => c.id === toId);
      if (fromIdx < 0 || toIdx < 0) return prev;
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      // Persist new positions.
      arr.forEach((c, i) => {
        fetch(`${API}/api/research/${pipelineId}/columns/${c.id}`, {
          method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ position: i }),
        }).catch(() => {});
      });
      return arr;
    });
  }, [pipelineId]);

  // ── PDF export ────────────────────────────────────────────────────────────
  // Mirrors the pipeline Status Report: open a styled print window and let the
  // browser print-to-PDF. Dependency-free.
  const exportPDF = useCallback(() => {
    const w = window.open('', '_blank');
    if (!w) { alert('Please allow popups to export the PDF.'); return; }
    const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const dateStr = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    const priLabel = (k) => ({ high: 'High', medium: 'Med', low: 'Low' }[k] || '');
    const statLabel = (s) => (s === 'sourced' ? 'Sourced' : 'Sourcing');
    const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '';

    // Value extractor per column id (mirrors renderCell, as plain text).
    const cellValue = (col, r) => {
      switch (col.id) {
        case 'status': return statLabel(r.status);
        case 'priority': return priLabel(r.priority);
        case 'assigned': return esc((r.assignees || []).map(a => a.name).join(', '));
        case 'candidates': return r.firm_id ? (Number(r.pool_candidate_count) || 0) : '';
        case 'linkedin': return (r.url || r.firm_linkedin_url) ? esc((r.url || r.firm_linkedin_url).replace(/^https?:\/\//, '').replace(/^www\./, '')) : '';
        case 'last_added': return fmtDate(r.last_candidate_added);
        case 'notes': return esc(r.notes || '');
        default: return esc((r.custom && r.custom[col.id]) ?? '');
      }
    };

    const headCols = ['Firm', ...columns.map(c => c.label)];
    const bodyRows = rows.map(r => {
      const cells = [
        `<span class="nm">${esc(r.firm_name)}</span>`,
        ...columns.map(c => cellValue(c, r)),
      ];
      return `<tr>${cells.map(v => `<td>${v}</td>`).join('')}</tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
      <title>Mapping — ${esc(searchName || 'Search')}</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box}
        body{font-family:'Segoe UI',Roboto,-apple-system,sans-serif;color:#1a1a1a;font-size:9px}
        .header{padding:24px 32px 16px;border-bottom:3px solid #059669}
        .header-row{display:flex;justify-content:space-between;align-items:flex-start}
        .header h1{font-size:20px;font-weight:700;color:#111827;letter-spacing:-0.5px}
        .header .sub{font-size:13px;color:#059669;font-weight:500;margin-top:2px}
        .header .meta{text-align:right;font-size:9px;color:#6b7280;line-height:1.5}
        .content{padding:16px 32px 32px}
        table{width:100%;border-collapse:collapse;table-layout:auto}
        th{text-align:left;padding:5px 7px;font-size:7px;text-transform:uppercase;letter-spacing:.6px;color:#6b7280;border-bottom:2px solid #e5e7eb;font-weight:600;background:#f9fafb;white-space:nowrap}
        td{padding:5px 7px;border-bottom:1px solid #f3f4f6;font-size:9px;vertical-align:top;word-break:break-word}
        tr:nth-child(even){background:#fafafa}
        .nm{font-weight:600;color:#111827}
        .footer{padding:12px 32px;border-top:1px solid #e5e7eb;font-size:7px;color:#9ca3af;display:flex;justify-content:space-between}
        @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
        @page{size:landscape;margin:0.3cm}
      </style></head><body>
      <div class="header"><div class="header-row">
        <div><h1>Mapping</h1><div class="sub">${esc(searchName || '')}</div></div>
        <div class="meta"><strong>Banyak Group</strong><br/>${dateStr}<br/>${rows.length} target firm${rows.length !== 1 ? 's' : ''}</div>
      </div></div>
      <div class="content">
        <table>
          <thead><tr>${headCols.map(h => `<th>${esc(h)}</th>`).join('')}</tr></thead>
          <tbody>${bodyRows || `<tr><td colspan="${headCols.length}" style="text-align:center;color:#9ca3af;padding:20px">No target firms</td></tr>`}</tbody>
        </table>
      </div>
      <div class="footer"><span>Banyak — Mapping</span><span>${dateStr}</span></div>
      </body></html>`;

    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 300);
  }, [rows, columns, searchName]);

  // ── Sorting ───────────────────────────────────────────────────────────────
  const handleSort = useCallback((key) => {
    setSortConfig(prev => {
      if (prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return { key: null, direction: 'asc' };
    });
  }, []);

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return rows;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    const priRank = { high: 3, medium: 2, low: 1, null: 0 };
    const val = (r) => {
      switch (sortConfig.key) {
        case 'firm_name': return (r.firm_name || '').toLowerCase();
        case 'status': return (r.status || 'sourcing');
        case 'priority': return priRank[r.priority || 'null'] || 0;
        case 'assigned': return ((r.assignees || [])[0]?.name || '').toLowerCase();
        case 'candidates': return Number(r.pool_candidate_count) || 0;
        case 'last_added': return r.last_candidate_added ? new Date(r.last_candidate_added).getTime() : 0;
        default: return '';
      }
    };
    return [...rows].sort((a, b) => {
      const av = val(a), bv = val(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rows, sortConfig]);

  // ── Analytics ─────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const total = rows.length;
    const byStatus = {};
    let assigned = 0, candidates = 0, highPri = 0;
    for (const r of rows) {
      byStatus[r.status || 'sourcing'] = (byStatus[r.status || 'sourcing'] || 0) + 1;
      if ((r.assignees || []).length > 0) assigned++;
      candidates += parseInt(r.pool_candidate_count) || 0;
      if (r.priority === 'high') highPri++;
    }
    return { total, byStatus, assigned, candidates, highPri };
  }, [rows]);

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600" /></div>;
  }

  const hCell = { backgroundColor: 'var(--list-header-bg)', color: 'var(--list-header-text)', borderBottom: '1px solid var(--list-header-border)' };

  return (
    <div className="h-full flex flex-col overflow-hidden bg-[color:var(--list-page-bg)]">
      {/* Analytics strip — same container as the pool tab toolbar */}
      <div className="flex-shrink-0 px-3 py-1.5 bg-[color:var(--list-page-bg)] flex items-center gap-5 overflow-x-auto">
        <MiniStat label="Target firms" value={stats.total} />
        <MiniStat label="Sourcing" value={stats.byStatus['sourcing'] || 0} dot="bg-amber-400" />
        <MiniStat label="Sourced" value={stats.byStatus['sourced'] || 0} dot="bg-emerald-500" />
        <MiniStat label="High priority" value={stats.highPri} dot="bg-red-500" />

        <div className="ml-auto flex items-center gap-2 flex-shrink-0">
          <button
            onClick={exportPDF}
            className="h-[30px] px-2.5 text-sm border rounded transition-colors bg-white border-gray-300 text-gray-600 hover:bg-gray-50 flex items-center gap-1.5"
            title="Export mapping to PDF"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            Export PDF
          </button>
          <button
            onClick={() => setNotesOpen(o => !o)}
            className="h-[30px] px-2.5 text-sm border rounded transition-colors bg-emerald-50 border-emerald-300 text-emerald-700 font-medium flex items-center gap-1.5"
            title={notesOpen ? 'Hide search notes' : 'Show search notes'}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {notesOpen
                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                : <><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></>}
            </svg>
            {notesOpen ? 'Hide notes' : 'Show notes'}
          </button>
        </div>
      </div>

      {/* Split: grid list (left) + freeform notes (right), draggable divider */}
      <div id="mapping-split" className="flex-1 min-h-0 flex overflow-hidden">
        {/* Table */}
        <div className="flex-1 min-w-0 min-h-0 ml-3 mb-1 bg-[color:var(--list-block-bg)] border rounded-lg shadow-sm overflow-auto">
          <table className="w-full text-sm border-collapse">
          <thead className="sticky top-0 z-10" style={{ backgroundColor: 'var(--list-header-bg)', color: 'var(--list-header-text)' }}>
            <tr className="text-left text-[10px] font-medium uppercase tracking-wider">
              <th className="px-2 py-2 text-center" style={{ width: 40, borderBottom: '1px solid var(--list-header-border)' }}>
                <input
                  type="checkbox"
                  checked={rows.length > 0 && selected.size === rows.length}
                  ref={el => { if (el) el.indeterminate = selected.size > 0 && selected.size < rows.length; }}
                  onChange={(e) => setSelected(e.target.checked ? new Set(rows.map(r => r.id)) : new Set())}
                  className="w-4 h-4 rounded border-gray-300 accent-emerald-600 focus:ring-emerald-500 cursor-pointer"
                />
              </th>
              <SortHeader label="Firm" sortKey="firm_name" sortConfig={sortConfig} onSort={handleSort} style={hCell} className="px-3 py-2 min-w-[200px]" />
              {columns.map(col => {
                const sortKey = BUILTIN_SORT[col.id];
                return (
                  <th
                    key={col.id}
                    className={`px-3 py-2 ${COL_WIDTH[col.id] || 'min-w-[120px]'} ${draggedCol === col.id ? 'opacity-40' : ''}`}
                    style={hCell}
                    draggable
                    onDragStart={(e) => { setDraggedCol(col.id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', col.id); }}
                    onDragEnter={(e) => e.preventDefault()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); if (draggedCol) reorderColumn(draggedCol, col.id); setDraggedCol(null); }}
                    onDragEnd={() => setDraggedCol(null)}
                  >
                    <div className="flex items-center gap-1 group cursor-move whitespace-nowrap">
                      <svg className="w-3 h-3 text-gray-300 group-hover:text-gray-400 flex-shrink-0 mr-0.5" fill="currentColor" viewBox="0 0 20 20"><path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"/></svg>
                      {sortKey ? (
                        <button onClick={() => handleSort(sortKey)} className="flex items-center gap-0.5 uppercase tracking-wider hover:opacity-80">
                          <span>{col.label}</span>
                          <svg className={`w-3 h-3 ${sortConfig.key === sortKey ? 'opacity-100' : 'opacity-25'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {sortConfig.key === sortKey && sortConfig.direction === 'asc'
                              ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" />
                              : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />}
                          </svg>
                        </button>
                      ) : <span>{col.label}</span>}
                      {!col.builtin && (
                        <button onClick={() => deleteColumn(col.id)} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500" title="Delete column">×</button>
                      )}
                    </div>
                  </th>
                );
              })}
              <th className="px-3 py-2 w-10" style={hCell}>
                <AddColumnButton onAdd={addColumn} />
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map(row => (
              <ResearchRow
                key={row.id}
                row={row}
                columns={columns}
                users={users}
                selected={selected.has(row.id)}
                onToggleSelect={() => toggleSel(row.id)}
                onPatch={patchRow}
                onPatchCustom={patchCustom}
                onDelete={deleteRow}
                onAddAssignee={addAssignee}
                onRemoveAssignee={removeAssignee}
              />
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={99} className="px-3 py-10 text-center text-sm text-gray-400">No target firms yet. Add a firm below to start mapping your search.</td></tr>
            )}
            {/* Add-firm row — sits directly under the last item and sticks to
                the bottom of the scroll area so it's always reachable. */}
            <tr className="sticky bottom-0 z-10" style={{ backgroundColor: 'var(--list-block-bg)', borderTop: '1px solid var(--list-header-border)' }}>
              <td colSpan={99} className="px-3 py-2">
                <AddFirmRow firms={firms} onAdd={addRow} />
              </td>
            </tr>
          </tbody>
        </table>
        </div>

        {/* Draggable divider + notes panel */}
        {notesOpen ? (
          <>
            <div
              onMouseDown={() => { draggingRef.current = true; document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none'; }}
              className="flex-shrink-0 cursor-col-resize flex items-center justify-center group"
              style={{ width: 20 }}
              title="Drag to resize"
            >
              <div className="w-1 h-10 rounded-full bg-gray-300 group-hover:bg-gray-400 transition-colors" />
            </div>
            <div className="flex-shrink-0 mb-1 mr-3 bg-white border rounded-lg shadow-sm overflow-hidden relative" style={{ width: notesWidth || 320 }}>
              <button
                onClick={() => setNotesOpen(false)}
                className="absolute top-2.5 right-3 z-10 p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                title="Hide notes"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </button>
              <MappingNotes pipelineId={pipelineId} />
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function SortHeader({ label, sortKey, sortConfig, onSort, style, className }) {
  const active = sortConfig.key === sortKey;
  return (
    <th className={className} style={style}>
      <button onClick={() => onSort(sortKey)} className="flex items-center gap-0.5 uppercase tracking-wider hover:opacity-80">
        <span>{label}</span>
        <svg className={`w-3 h-3 ${active ? 'opacity-100' : 'opacity-25'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {active && sortConfig.direction === 'asc'
            ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" />
            : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />}
        </svg>
      </button>
    </th>
  );
}

function MiniStat({ label, value, dot }) {
  return (
    <div className="flex items-center gap-1.5 flex-shrink-0">
      {dot && <span className={`w-2 h-2 rounded-full ${dot}`} />}
      <span className="text-sm font-semibold text-gray-900 leading-none">{value}</span>
      <span className="text-xs text-gray-500 leading-none">{label}</span>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="flex-shrink-0">
      <div className="text-lg font-semibold text-gray-900 leading-none">{value}</div>
      <div className="text-[10px] text-gray-400 uppercase tracking-wide mt-0.5">{label}</div>
    </div>
  );
}

// Renders the cell content for a given column (builtin id or custom column).
function renderCell(col, { row, users, onPatch, onPatchCustom, onAddAssignee, onRemoveAssignee }) {
  switch (col.id) {
    case 'status':
      return <StatusToggle value={row.status || 'sourcing'} onChange={(v) => onPatch(row.id, { status: v })} />;
    case 'priority':
      return <PrioritySelect value={row.priority || 'none'} onChange={(v) => onPatch(row.id, { priority: v === 'none' ? null : v })} />;
    case 'assigned':
      return <AssigneeSelect users={users} assignees={row.assignees || []} onAdd={(u) => onAddAssignee(row.id, u)} onRemove={(uid) => onRemoveAssignee(row.id, uid)} />;
    case 'candidates':
      return row.firm_id
        ? <span className={`text-sm font-medium ${Number(row.pool_candidate_count) > 0 ? 'text-emerald-700' : 'text-gray-400'}`}>{Number(row.pool_candidate_count) || 0}</span>
        : <span className="text-sm text-gray-300" title="Link this row to a firm to count candidates">—</span>;
    case 'linkedin': {
      const url = row.url || row.firm_linkedin_url;
      return url
        ? <a href={url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline truncate inline-block max-w-[140px]">{url.replace(/^https?:\/\//, '').replace(/^www\./, '')}</a>
        : (row.firm_id
            ? <span className="text-xs text-gray-300" title="No LinkedIn URL on this firm's profile">—</span>
            : <input placeholder="add url" defaultValue="" onBlur={(e) => e.target.value && onPatch(row.id, { url: e.target.value })}
                className="w-24 bg-transparent text-xs text-gray-400 focus:outline-none focus:bg-white focus:ring-1 focus:ring-emerald-400 rounded px-1" />);
    }
    case 'last_added':
      return row.last_candidate_added
        ? <span className="text-xs text-gray-600" title={new Date(row.last_candidate_added).toLocaleString()}>{new Date(row.last_candidate_added).toLocaleDateString()}</span>
        : <span className="text-xs text-gray-300">—</span>;
    case 'notes':
      return <input defaultValue={row.notes || ''} placeholder="…"
        onBlur={(e) => e.target.value !== (row.notes || '') && onPatch(row.id, { notes: e.target.value })}
        className="w-full bg-transparent text-xs text-gray-600 focus:outline-none focus:bg-white focus:ring-1 focus:ring-emerald-400 rounded px-1" />;
    default:
      // Custom column → typed input stored in row.custom.
      return <input
        type={col.kind === 'number' ? 'number' : col.kind === 'date' ? 'date' : 'text'}
        defaultValue={(row.custom && row.custom[col.id]) ?? ''}
        onBlur={(e) => onPatchCustom(row.id, col.id, e.target.value)}
        className="w-full bg-transparent text-xs text-gray-600 focus:outline-none focus:bg-white focus:ring-1 focus:ring-emerald-400 rounded px-1"
      />;
  }
}

function ResearchRow({ row, columns, users, selected, onToggleSelect, onPatch, onPatchCustom, onDelete, onAddAssignee, onRemoveAssignee }) {
  return (
    <tr
      className={`group border-b transition-colors ${selected ? 'bg-emerald-100/60' : 'hover:bg-emerald-50/50'}`}
      style={{ borderColor: 'var(--list-header-border)' }}
    >
      <td className="px-2 py-2 text-center" style={{ width: 40 }}>
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          className="w-4 h-4 rounded border-gray-300 accent-emerald-600 focus:ring-emerald-500 cursor-pointer"
        />
      </td>
      <td className="px-3 py-1.5">
        <div className="flex items-center gap-1">
          <button onClick={() => onDelete(row.id)} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 text-sm leading-none flex-shrink-0" title="Delete row">×</button>
          <div className="flex-1 min-w-0">
            <input
              defaultValue={row.firm_name}
              onBlur={(e) => e.target.value !== row.firm_name && onPatch(row.id, { firm_name: e.target.value })}
              className="w-full bg-transparent text-sm font-medium text-gray-900 focus:outline-none focus:bg-white focus:ring-1 focus:ring-emerald-400 rounded px-1"
            />
            {row.firm_id && <span className="text-[9px] text-emerald-600 px-1" title="Linked firm">● linked</span>}
          </div>
        </div>
      </td>
      {columns.map(col => (
        <td key={col.id} className="px-3 py-1.5">
          {renderCell(col, { row, users, onPatch, onPatchCustom, onAddAssignee, onRemoveAssignee })}
        </td>
      ))}
      <td />
    </tr>
  );
}

function StatusToggle({ value, onChange }) {
  // Inline two-state toggle so you can "cross off" a firm with one click.
  const isSourced = value === 'sourced';
  return (
    <button
      onClick={() => onChange(isSourced ? 'sourcing' : 'sourced')}
      className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border transition-colors ${
        isSourced
          ? 'bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-200'
          : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
      }`}
      title={isSourced ? 'Sourced — click to reopen' : 'Sourcing — click to mark sourced'}
    >
      {isSourced ? (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
      ) : (
        <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />
      )}
      {isSourced ? 'Sourced' : 'Sourcing'}
    </button>
  );
}

function PrioritySelect({ value, onChange }) {
  // Single button showing the current priority (None default) + a dropdown
  // listing None/High/Med/Low, each with a colored dot.
  const [open, setOpen] = useState(false);
  const isNone = !value || value === 'none';
  const shown = PRIORITY_OPTIONS.find(p => p.key === (isNone ? 'none' : value)) || PRIORITY_OPTIONS[0];

  return (
    <div className="relative inline-block">
      <button
        onClick={() => setOpen(o => !o)}
        className={`px-2 py-1 text-[10px] font-medium rounded border transition-all flex items-center gap-1 ${!isNone ? shown.bg : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'}`}
      >
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${shown.dot}`}></span>
        {shown.label}
        <svg className={`w-2.5 h-2.5 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 min-w-[100px]">
            {PRIORITY_OPTIONS.map(p => (
              <button key={p.key}
                onClick={() => { setOpen(false); onChange(p.key); }}
                className={`w-full text-left px-3 py-1.5 text-[10px] font-medium flex items-center gap-1.5 hover:bg-gray-50 ${shown.key === p.key ? 'bg-gray-50' : ''}`}>
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${p.dot}`}></span>
                {p.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function AssigneeSelect({ users, assignees = [], onAdd, onRemove }) {
  // Multiple assignees: avatar pills (each removable) + a dashed "+" that
  // opens a searchable picker. Styled like the preview panel's AssignPills.
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setPicking(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const initials = (name) => (name || '?').trim().charAt(0).toUpperCase();
  const assignedIds = new Set(assignees.map(a => a.id));
  const matches = users
    .filter(u => !assignedIds.has(u.id))
    .filter(u => { const q = query.trim().toLowerCase(); return !q || (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q); })
    .slice(0, 30);

  return (
    <div ref={ref} className="relative">
      <div className="flex items-center gap-1 flex-wrap">
        {assignees.map(a => (
          <span key={a.id} className="inline-flex items-center bg-blue-50 rounded-full pr-1" title={a.name}>
            <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-[9px] font-bold flex items-center justify-center overflow-hidden">
              {a.avatar_url ? <img src={a.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover" /> : initials(a.name)}
            </span>
            <button onClick={() => onRemove(a.id)} className="ml-0.5 text-blue-400 hover:text-blue-700" title={`Remove ${a.name}`}>
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </span>
        ))}
        <button onClick={() => setPicking(p => !p)}
          className="w-6 h-6 rounded-full border border-dashed border-gray-300 text-gray-400 hover:border-blue-300 hover:text-blue-500 flex items-center justify-center flex-shrink-0"
          title="Assign someone">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
        </button>
      </div>

      {picking && (
        <div className="absolute z-20 mt-1 left-0 w-56 bg-white border border-gray-200 rounded-lg shadow-lg p-2">
          <input type="text" autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search team…"
            className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <div className="mt-1 max-h-44 overflow-y-auto">
            {matches.length === 0 ? (
              <p className="text-[11px] text-gray-400 text-center py-3">No one to add</p>
            ) : matches.map(u => (
              <button key={u.id} onClick={() => { onAdd(u); setQuery(''); }}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-left rounded hover:bg-blue-50">
                <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-600 text-[9px] font-bold flex items-center justify-center overflow-hidden flex-shrink-0">
                  {u.avatar_url ? <img src={u.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover" /> : initials(u.name)}
                </span>
                <span className="min-w-0">
                  <span className="block text-xs text-gray-800 truncate">{u.name}</span>
                  {u.email && <span className="block text-[10px] text-gray-400 truncate">{u.email}</span>}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AddFirmRow({ firms, onAdd }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);

  const matches = useMemo(() => {
    if (!q.trim()) return [];
    const ql = q.toLowerCase();
    return firms.filter(f => (f.name || '').toLowerCase().includes(ql)).slice(0, 6);
  }, [q, firms]);

  // The selectable list = matches + a trailing "Add as new" entry (when typing).
  const hasAddNew = q.trim().length > 0;
  const itemCount = matches.length + (hasAddNew ? 1 : 0);

  // Reset highlight to the top whenever the query changes.
  useEffect(() => { setActive(0); }, [q]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pickAt = (idx) => {
    const pick = idx < matches.length ? matches[idx] : { id: null, name: q.trim() };
    onAdd(pick); setQ(''); setOpen(false); setActive(0);
  };

  return (
    <div className="relative flex items-center gap-2" ref={boxRef}>
      <input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onKeyDown={(e) => {
          if (!q.trim() && e.key !== 'ArrowDown') return;
          if (e.key === 'ArrowDown') {
            e.preventDefault(); setOpen(true);
            setActive(a => Math.min(a + 1, Math.max(0, itemCount - 1)));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive(a => Math.max(a - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            pickAt(active);
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
        placeholder="+ Add a target firm (search your firms, or type a new name + Enter)"
        className="flex-1 text-sm bg-transparent focus:outline-none text-gray-700 placeholder-gray-400"
      />
      {open && itemCount > 0 && (
        <div className="absolute bottom-full left-6 mb-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-20">
          {matches.map((f, idx) => (
            <button key={f.id} onClick={() => pickAt(idx)} onMouseEnter={() => setActive(idx)}
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between ${active === idx ? 'bg-emerald-50' : ''}`}>
              <span className="text-gray-800">{f.name}</span>
              <span className="text-[10px] text-emerald-600">{active === idx ? '↵ link' : 'link'}</span>
            </button>
          ))}
          {hasAddNew && (
            <button onClick={() => pickAt(matches.length)} onMouseEnter={() => setActive(matches.length)}
              className={`w-full text-left px-3 py-2 text-sm border-t border-gray-100 text-gray-500 flex items-center justify-between ${active === matches.length ? 'bg-gray-50' : ''}`}>
              <span>Add "{q.trim()}" as new</span>
              {active === matches.length && <span className="text-[10px] text-gray-400">↵</span>}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function AddColumnButton({ onAdd }) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [kind, setKind] = useState('text');
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)} title="Add column" className="text-gray-300 hover:text-emerald-600 text-base leading-none">+</button>
      {open && (
        <div className="absolute top-full right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-20 normal-case">
          <input autoFocus value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Column name"
            className="w-full text-sm border border-gray-300 rounded px-2 py-1 mb-2 focus:outline-none focus:ring-1 focus:ring-emerald-400" />
          <select value={kind} onChange={(e) => setKind(e.target.value)}
            className="w-full text-sm border border-gray-300 rounded px-2 py-1 mb-2">
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="date">Date</option>
            <option value="url">URL</option>
          </select>
          <button onClick={() => { if (label.trim()) { onAdd(label.trim(), kind); setLabel(''); setKind('text'); setOpen(false); } }}
            className="w-full text-sm font-medium text-white bg-emerald-600 rounded py-1 hover:bg-emerald-700">Add column</button>
        </div>
      )}
    </div>
  );
}
